/**
 * Etapa 2 do cadastro de instalação elétrica: inspeções e manutenções (issue #364, D4).
 *
 * Quem pode alterar registra, edita e exclui; quem só consulta vê a tabela. A "próxima prevista"
 * só existe em inspeção: o servidor recusa em manutenção, então a tela nem oferece o campo.
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { ConfirmDialog } from '../../../../../components/common/ConfirmDialog';
import { DataTable, type DataTableColumn } from '../../../../../components/common/DataTable';
import { electricalInstallationService } from '../../../../../services/electricalInstallationService';
import {
  ELECTRICAL_RECORD_RESULTS,
  ELECTRICAL_RECORD_TYPES,
  ROTULO_DO_RESULTADO,
  ROTULO_DO_TIPO_DE_REGISTRO,
  type ElectricalRecord,
  type ElectricalRecordFormData,
  type ElectricalRecordResult,
  type ElectricalRecordType,
} from '../../../../../types/electricalInstallation';
import { OverdueChip, ResultChip } from '../../ElectricalChips';
import type {
  ListStepProps,
  ResultadoDoEnvio,
  StepHandle,
} from '../ElectricalInstallationWizard.types';
import { errorMessage } from '../electricalWizardValidation';
import { ehDataReal, formatarData, hojeEmTexto, MENSAGEM_DE_DATA } from './formatosDasEtapas';

interface RecordForm {
  recordType: ElectricalRecordType;
  performedAt: string;
  responsible: string;
  result: ElectricalRecordResult | '';
  nextDueDate: string;
  observations: string;
}

type RecordFormErrors = Partial<Record<keyof RecordForm, string>>;

const EMPTY_FORM: RecordForm = {
  recordType: 'INSPECAO',
  performedAt: '',
  responsible: '',
  result: '',
  nextDueDate: '',
  observations: '',
};

const formFromRecord = (record: ElectricalRecord): RecordForm => ({
  recordType: record.recordType,
  performedAt: record.performedAt,
  responsible: record.responsible ?? '',
  result: record.result,
  nextDueDate: record.nextDueDate ?? '',
  observations: record.observations ?? '',
});

const validateForm = (form: RecordForm): RecordFormErrors => {
  const errors: RecordFormErrors = {};
  if (!ehDataReal(form.performedAt.trim())) errors.performedAt = MENSAGEM_DE_DATA;
  if (form.responsible.trim() === '') errors.responsible = 'Diga quem fez.';
  else if (form.responsible.trim().length > 200) errors.responsible = 'Use até 200 letras.';
  if (form.result === '') errors.result = 'Escolha o resultado.';
  if (
    form.recordType === 'INSPECAO' &&
    form.nextDueDate.trim() !== '' &&
    !ehDataReal(form.nextDueDate.trim())
  ) {
    errors.nextDueDate = MENSAGEM_DE_DATA;
  }
  return errors;
};

const formToPayload = (form: RecordForm): ElectricalRecordFormData => ({
  recordType: form.recordType,
  performedAt: form.performedAt.trim(),
  responsible: form.responsible.trim(),
  result: form.result as ElectricalRecordResult,
  nextDueDate:
    form.recordType === 'INSPECAO' && form.nextDueDate.trim() !== ''
      ? form.nextDueDate.trim()
      : null,
  observations: form.observations.trim() === '' ? null : form.observations.trim(),
});

const newestFirst = (records: ElectricalRecord[]): ElectricalRecord[] =>
  [...records].sort((a, b) =>
    a.performedAt < b.performedAt ? 1 : a.performedAt > b.performedAt ? -1 : b.id - a.id
  );

export const InspectionsStep = forwardRef<StepHandle, ListStepProps>(function InspectionsStep(
  { installationId, canEdit, onDirtyChange, onSaved },
  ref
) {
  const [records, setRecords] = useState<ElectricalRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [form, setForm] = useState<RecordForm | null>(null);
  const [initialForm, setInitialForm] = useState<RecordForm>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [errors, setErrors] = useState<RecordFormErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ElectricalRecord | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const dirty = form !== null && JSON.stringify(form) !== JSON.stringify(initialForm);

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    electricalInstallationService
      .getRecords(installationId)
      .then(found => {
        if (active) setRecords(newestFirst(found));
      })
      .catch((err: unknown) => {
        if (active)
          setLoadError(errorMessage(err, 'Não foi possível carregar as inspeções e manutenções.'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [installationId]);

  const openForm = useCallback((record?: ElectricalRecord) => {
    const start = record ? formFromRecord(record) : EMPTY_FORM;
    setForm(start);
    setInitialForm(start);
    setEditingId(record?.id ?? null);
    setErrors({});
    setFormError(null);
  }, []);

  const closeForm = () => {
    setForm(null);
    setEditingId(null);
    setErrors({});
    setFormError(null);
  };

  const patchForm = (patch: Partial<RecordForm>) => {
    setForm(previous => (previous ? { ...previous, ...patch } : previous));
    setErrors(previous => {
      const next = { ...previous };
      (Object.keys(patch) as Array<keyof RecordForm>).forEach(key => delete next[key]);
      return next;
    });
    setFormError(null);
  };

  const submit = useCallback(async (): Promise<ResultadoDoEnvio> => {
    if (!form) return 'enviado';
    const found = validateForm(form);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setFormError('Corrija os campos marcados para guardar o registro.');
      return 'incompleto';
    }
    setSaving(true);
    setFormError(null);
    try {
      const payload = formToPayload(form);
      const saved =
        editingId === null
          ? await electricalInstallationService.createRecord(installationId, payload)
          : await electricalInstallationService.updateRecord(installationId, editingId, payload);
      setRecords(previous =>
        newestFirst([...previous.filter(record => record.id !== saved.id), saved])
      );
      onSaved(editingId === null ? 'Registro guardado.' : 'Registro atualizado.');
      setForm(null);
      setEditingId(null);
      return 'enviado';
    } catch (err: unknown) {
      // O servidor sabe a regra (data futura, previsão anterior à realização): a tela repete a frase dele.
      setFormError(errorMessage(err, 'Não foi possível guardar o registro.'));
      return 'recusado';
    } finally {
      setSaving(false);
    }
  }, [form, editingId, installationId, onSaved]);

  useImperativeHandle(
    ref,
    () => ({
      salvarPendente: async () => {
        if (!dirty) return 'enviado';
        return submit();
      },
    }),
    [dirty, submit]
  );

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await electricalInstallationService.deleteRecord(installationId, deleteTarget.id);
      setRecords(previous => previous.filter(record => record.id !== deleteTarget.id));
      onSaved('Registro excluído.');
      setDeleteTarget(null);
    } catch (err: unknown) {
      setDeleteError(errorMessage(err, 'Não foi possível excluir o registro.'));
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo<DataTableColumn<ElectricalRecord>[]>(() => {
    const hoje = hojeEmTexto();
    const base: DataTableColumn<ElectricalRecord>[] = [
      {
        id: 'type',
        label: 'Tipo',
        primary: true,
        render: record => (
          <Typography variant='body1' fontWeight='medium'>
            {ROTULO_DO_TIPO_DE_REGISTRO[record.recordType]}
          </Typography>
        ),
      },
      { id: 'performedAt', label: 'Data', render: record => formatarData(record.performedAt) },
      { id: 'responsible', label: 'Responsável', render: record => record.responsible ?? '—' },
      { id: 'result', label: 'Resultado', render: record => <ResultChip result={record.result} /> },
      {
        id: 'nextDueDate',
        label: 'Próxima prevista',
        render: record => {
          if (record.recordType !== 'INSPECAO' || !record.nextDueDate) return '—';
          if (record.nextDueDate >= hoje) return formatarData(record.nextDueDate);
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
              <span>{formatarData(record.nextDueDate)}</span>
              <OverdueChip label='Vencida' />
            </Box>
          );
        },
      },
      {
        id: 'observations',
        label: 'Observação',
        hideOnMobile: true,
        render: record => record.observations ?? '—',
      },
    ];
    if (!canEdit) return base;
    return [
      ...base,
      {
        id: 'actions',
        label: 'Ações',
        align: 'center',
        render: record => {
          const rotulo = `${ROTULO_DO_TIPO_DE_REGISTRO[record.recordType]} de ${formatarData(record.performedAt)}`;
          return (
            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
              <Tooltip title='Editar'>
                <IconButton
                  aria-label={`Editar ${rotulo}`}
                  onClick={() => openForm(record)}
                  sx={{ width: 44, height: 44 }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title='Excluir'>
                <IconButton
                  aria-label={`Excluir ${rotulo}`}
                  color='error'
                  onClick={() => {
                    setDeleteError(null);
                    setDeleteTarget(record);
                  }}
                  sx={{ width: 44, height: 44 }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
    ];
  }, [canEdit, openForm]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    void submit();
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ maxWidth: 640 }}>
          <Typography variant='h6' component='h3'>
            Inspeções e manutenções
          </Typography>
          <Typography variant='body1' color='text.secondary'>
            {canEdit
              ? 'Registre cada inspeção e cada manutenção feita nesta instalação. A data da próxima inspeção prevista alimenta o aviso de prazo da lista.'
              : 'Aqui estão as inspeções e manutenções já registradas nesta instalação.'}
          </Typography>
        </Box>
        {canEdit && form === null && (
          <Button
            variant='outlined'
            startIcon={<AddIcon />}
            onClick={() => openForm()}
            sx={{ minHeight: 44 }}
          >
            Registrar inspeção ou manutenção
          </Button>
        )}
      </Box>

      {loadError && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}

      {canEdit && form !== null && (
        <Paper
          variant='outlined'
          component='form'
          noValidate
          sx={{ p: 2, mb: 2 }}
          onSubmit={handleSubmit}
        >
          <Typography variant='subtitle1' component='h4' fontWeight={700} sx={{ mb: 2 }}>
            {editingId === null ? 'Novo registro' : 'Editar registro'}
          </Typography>
          {formError && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: 2,
            }}
          >
            <TextField
              select
              required
              label='Tipo do registro'
              value={form.recordType}
              onChange={event => {
                const recordType = event.target.value as ElectricalRecordType;
                patchForm(
                  recordType === 'INSPECAO' ? { recordType } : { recordType, nextDueDate: '' }
                );
              }}
              disabled={saving}
            >
              {ELECTRICAL_RECORD_TYPES.map(type => (
                <MenuItem key={type} value={type} sx={{ minHeight: 44 }}>
                  {ROTULO_DO_TIPO_DE_REGISTRO[type]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              required
              label='Data em que foi feito'
              placeholder='AAAA-MM-DD'
              value={form.performedAt}
              onChange={event => patchForm({ performedAt: event.target.value })}
              error={Boolean(errors.performedAt)}
              helperText={
                errors.performedAt ?? 'Ano-mês-dia, como 2026-03-12. Não pode ser uma data futura.'
              }
              disabled={saving}
            />
            <TextField
              required
              label='Responsável'
              value={form.responsible}
              onChange={event => patchForm({ responsible: event.target.value })}
              error={Boolean(errors.responsible)}
              helperText={errors.responsible ?? 'Quem fez a inspeção ou a manutenção.'}
              disabled={saving}
            />
            <TextField
              select
              required
              label='Resultado'
              value={form.result}
              onChange={event =>
                patchForm({ result: event.target.value as ElectricalRecordResult })
              }
              error={Boolean(errors.result)}
              helperText={errors.result}
              disabled={saving}
            >
              {ELECTRICAL_RECORD_RESULTS.map(result => (
                <MenuItem key={result} value={result} sx={{ minHeight: 44 }}>
                  {ROTULO_DO_RESULTADO[result]}
                </MenuItem>
              ))}
            </TextField>
            {form.recordType === 'INSPECAO' && (
              <TextField
                label='Próxima prevista'
                placeholder='AAAA-MM-DD'
                value={form.nextDueDate}
                onChange={event => patchForm({ nextDueDate: event.target.value })}
                error={Boolean(errors.nextDueDate)}
                helperText={
                  errors.nextDueDate ??
                  'Quando a inspeção deve se repetir. Deixe em branco se ainda não foi marcada.'
                }
                disabled={saving}
              />
            )}
            <TextField
              label='Observações'
              multiline
              minRows={2}
              value={form.observations}
              onChange={event => patchForm({ observations: event.target.value })}
              disabled={saving}
              sx={{ gridColumn: { md: '1 / -1' } }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
            <Button onClick={closeForm} disabled={saving} sx={{ minHeight: 44 }}>
              Cancelar
            </Button>
            <Button type='submit' variant='contained' disabled={saving} sx={{ minHeight: 44 }}>
              {editingId === null ? 'Guardar registro' : 'Guardar alterações'}
            </Button>
          </Box>
        </Paper>
      )}

      <DataTable
        embedded
        columns={columns}
        rows={records}
        getRowId={record => record.id}
        loading={loading}
        emptyState={
          canEdit
            ? {
                title: 'Nenhuma inspeção ou manutenção registrada',
                description:
                  'Este é o histórico da instalação: cada inspeção e cada manutenção entra aqui, com data, responsável e resultado. É ele que mostra, na lista, se a próxima inspeção está em dia. Use "Registrar inspeção ou manutenção" para começar; dá para pular e voltar depois.',
              }
            : {
                title: 'Nenhuma inspeção ou manutenção registrada',
                description:
                  'Este é o histórico da instalação: cada inspeção e cada manutenção entra aqui, com data, responsável e resultado. Nada foi registrado ainda.',
              }
        }
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        title='Excluir este registro?'
        message={
          deleteTarget
            ? `${ROTULO_DO_TIPO_DE_REGISTRO[deleteTarget.recordType]} de ${formatarData(deleteTarget.performedAt)}. Ele sai do histórico e não dá para desfazer.`
            : ''
        }
        confirmLabel='Excluir registro'
        loading={deleting}
        error={deleteError}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
});

export default InspectionsStep;
